import type { ListPageConfig } from '../../core/types';

/**
 * Builds the bookings list config with a fresh "upcoming" cutoff (now).
 * Call this at page initialization (e.g. inside a useMemo on mount) so the
 * cutoff is recomputed each time the page is opened, instead of being frozen
 * to module-load time.
 *
 * The "upcoming" filter is registered as a `defaultFilterCondition` (visible
 * removable chip), not a hidden `defaultFilters.filter_groups` entry, so the
 * user can clear it from the filter bar to see past bookings — same UX shape
 * as the booking-types list.
 *
 * The page-level `BookingsPage` additionally appends a "host = me" condition
 * once the current user's `OrgMembership.entity_id` resolves; that condition
 * isn't here because it's per-user.
 */
export function buildBookingListConfig(): ListPageConfig {
  const nowIso = new Date().toISOString();
  return {
    entityType: 'ahoy_booking',
    title: 'Booking',
    pluralTitle: 'Bookings',

    columns: [
      { kind: 'property', name: 'ahoy_start_time', sortable: true, width: 200 },
      { kind: 'association', name: 'ahoy_booking_to_booking_type' },
      // Host before booker so multi-host orgs can scan "whose meeting is this"
      // without checking every row's row-level slideout.
      { kind: 'association', name: 'ahoy_booking_to_host' },
      { kind: 'property', name: 'ahoy_booker_name' },
      { kind: 'property', name: 'ahoy_booker_email' },
      { kind: 'property', name: 'ahoy_status' },
      { kind: 'property', name: 'ahoy_location_type' },
    ],

    // Bookings are created externally via the public booking flow; no internal create.
    createProperties: [],
    createAssociations: [],

    metrics: [],

    defaultFilters: {
      page: 1,
      page_size: 20,
      sort: 'ahoy_start_time',
      order: 'asc',
    },

    // Visible/removable defaults — appear as chips in the filter bar.
    // Frontend OperatorType (`'greater_than_or_equal'`) is translated to
    // backend `'gte'` by `convertConditionGroupsToFilterGroups` when
    // `useListState` wraps these into a single group at apply time.
    defaultFilterConditions: [
      {
        id: 'default-upcoming',
        field: 'ahoy_start_time',
        operator: 'greater_than_or_equal',
        value: nowIso,
      },
    ],

    showTimeFilter: false,
  };
}
